// ============================================================
// 料金の再計算（補償の追加・削除 / 日程変更）— 2026-10-03 前田さん
//
// 「CDW/NOC の計算そのものは簡単なはず。結果を人間がチェックすればいい」
// → AI が計算して**確認カードで人が承認**してから金額フィールドを書く（apply_price_change）。
//
// 本番データ(2026-10-03 調査)で分かった保存形式:
//   sum = base(定価) − coupon(割引額) + option + noc + cdw + 残差(深夜早朝料金など)
//   例: TR-SYNHEE 28,000 + 990 + 深夜 4,400 = 33,390
// 補償の日額は**車種の価格帯**で決まる（矛盾ではなかった）:
//   エクリプス等 550/1,650 円・デリカ/ハイエース等 770/2,200 円 → 料金計算 API が車種ごとに返す。
//
// ここは**純粋な計算だけ**（取得・保存は tools.ts）。テストで式を固定する。
// ============================================================

export interface PriceParts {
  base: number;     // 基本料金（割引前）
  coupon: number;   // 割引額（キャンペーン等）
  option: number;   // チャイルド/ブースター等
  noc: number;
  cdw: number;
  other: number;    // 合計の残差（深夜早朝料金など、個別フィールドが無いもの）
  sum: number;
}

export interface CalcSnapshot {
  base_subtotal: number;  // 割引前の基本料金
  options: number;
  fees: number;           // 深夜早朝料金
  noc: number;            // その期間の NOC 総額（加入した場合）
  cdw: number;            // その期間の CDW 総額
  days: number;           // オプション日数
}

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function currentParts(detail: any): PriceParts {
  const base = num(detail?.base_price);
  const coupon = num(detail?.coupon);
  const option = num(detail?.option_price);
  const noc = num(detail?.noc_price);
  const cdw = num(detail?.cdw_price);
  const sum = num(detail?.sum_price);
  return { base, coupon, option, noc, cdw, other: sum - (base - coupon + option + noc + cdw), sum };
}

export function sumOf(p: Omit<PriceParts, 'sum'>): number {
  return p.base - p.coupon + p.option + p.noc + p.cdw + p.other;
}

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

export interface Proposal {
  parts: PriceParts;
  lines: string[];          // 計算根拠（カードとメモに出す）
  needs_decision?: string;  // 単価が決められないとき（AI は書き込まずユーザーに聞く）
}

/**
 * 自社LP: 料金計算 API（公開サイトと同じロジック）で再計算する。
 *  - 補償だけの変更: 基本料金・割引・オプション・残差は**触らない**。補償額だけ calc の値に差し替え
 *  - 日程変更: 基本・オプション・深夜早朝を新日程で再計算。割引は**予約時の割引率を維持**
 *    （予約時のキャンペーンが今は終わっていても、お客様の条件を変えない）
 */
export function proposeLp(input: {
  cur: PriceParts; calcNew: CalcSnapshot; datesChanged: boolean; wantNoc: boolean; wantCdw: boolean;
}): Proposal {
  const { cur, calcNew, datesChanged, wantNoc, wantCdw } = input;
  const lines: string[] = ['自社LP: 料金計算 API（公開サイトと同じ計算）で算出'];
  let base = cur.base; let coupon = cur.coupon; let option = cur.option; let other = cur.other;
  if (datesChanged) {
    base = calcNew.base_subtotal;
    coupon = cur.base > 0 ? Math.round((cur.coupon / cur.base) * base) : 0;
    option = calcNew.options;
    other = calcNew.fees;
    lines.push(`基本料金: 新日程で再計算 ${yen(cur.base)} → ${yen(base)}`);
    if (cur.coupon > 0) {
      lines.push(`割引: 予約時の割引率 ${Math.round((cur.coupon / cur.base) * 1000) / 10}% を維持 ${yen(cur.coupon)} → ${yen(coupon)}`);
    }
    if (cur.option !== option) lines.push(`オプション: 新日程で再計算 ${yen(cur.option)} → ${yen(option)}`);
    if (cur.other !== other) lines.push(`深夜早朝料金など: ${yen(cur.other)}（合計の残差）→ ${yen(other)}（新しい時刻で計算）`);
  }
  const noc = wantNoc ? calcNew.noc : 0;
  const cdw = wantCdw ? calcNew.cdw : 0;
  if (wantNoc) lines.push(`NOC: ${yen(calcNew.noc / Math.max(1, calcNew.days))}/日 × ${calcNew.days}日 = ${yen(noc)}（車種の価格帯の単価）`);
  if (wantCdw) lines.push(`CDW: ${yen(calcNew.cdw / Math.max(1, calcNew.days))}/日 × ${calcNew.days}日 = ${yen(cdw)}（車種の価格帯の単価）`);
  const parts = { base, coupon, option, noc, cdw, other, sum: 0 };
  parts.sum = sumOf(parts);
  return { parts, lines };
}

/**
 * OTA: 基本料金・割引・オプションは日割り（実日数比）。補償は 日額 × 実日数。残差（深夜等）は据置。
 * 補償の日額は 料金計算 API の車種単価（unit）を使う。取れなければ既存の日額、それも無ければ決められない。
 */
export function proposeOta(input: {
  cur: PriceParts; curDays: number; newDays: number; wantNoc: boolean; wantCdw: boolean;
  unit: { noc: number; cdw: number; source: string } | null;
}): Proposal {
  const { cur, curDays, newDays, wantNoc, wantCdw } = input;
  const lines: string[] = ['OTA: 基本料金・割引・オプションは日割り、補償は 日額 × 実日数'];
  const datesChanged = curDays !== newDays;
  const prorate = (a: number) => (datesChanged ? Math.round((a / Math.max(1, curDays)) * newDays) : a);
  const base = prorate(cur.base);
  const coupon = prorate(cur.coupon);
  const option = prorate(cur.option);
  if (datesChanged) {
    lines.push(`実日数: ${curDays}日 → ${newDays}日`);
    lines.push(`基本料金: ${yen(cur.base)} ÷ ${curDays} × ${newDays} = ${yen(base)}`);
    if (cur.coupon) lines.push(`割引: ${yen(cur.coupon)} ÷ ${curDays} × ${newDays} = ${yen(coupon)}`);
    if (cur.option) lines.push(`オプション: ${yen(cur.option)} ÷ ${curDays} × ${newDays} = ${yen(option)}`);
  }
  // 日額: calc の車種単価 > 既存の日額（その補償が既に付いていれば）
  const existingPerDay = (amount: number) => (amount > 0 ? Math.round(amount / Math.max(1, curDays)) : 0);
  const perDay = (kind: 'noc' | 'cdw'): { v: number; src: string } | null => {
    if (input.unit && input.unit[kind] > 0) return { v: input.unit[kind], src: input.unit.source };
    const e = existingPerDay(cur[kind]);
    return e > 0 ? { v: e, src: 'この予約の既存の日額' } : null;
  };
  const missing: string[] = [];
  let noc = 0; let cdw = 0;
  for (const kind of ['noc', 'cdw'] as const) {
    const want = kind === 'noc' ? wantNoc : wantCdw;
    if (!want) continue;
    const u = perDay(kind);
    if (!u) { missing.push(kind.toUpperCase()); continue; }
    const amount = u.v * newDays;
    if (kind === 'noc') noc = amount; else cdw = amount;
    lines.push(`${kind.toUpperCase()}: ${yen(u.v)}/日 × ${newDays}日 = ${yen(amount)}（${u.src}）`);
  }
  if (cur.other !== 0) lines.push(`その他（合計の残差。那覇は深夜早朝料金など）${yen(cur.other)} は据置`);
  const parts = { base, coupon, option, noc, cdw, other: cur.other, sum: 0 };
  parts.sum = sumOf(parts);
  return {
    parts, lines,
    ...(missing.length ? {
      needs_decision: `${missing.join('/')} の日額を車種から決められませんでした（料金計算 API に無い車種）。`
        + '日額をユーザーに確認し、noc_per_day / cdw_per_day で指定してください'
        + '（店舗で違う: 那覇 550/1,650 または 770/2,200・宮古 NOC 550 / CDW 1,650 または 1,100）',
    } : {}),
  };
}

/** カード・結果用: 変化した金額項目だけを before→after で返す */
export function diffParts(cur: PriceParts, next: PriceParts): Array<{ key: keyof PriceParts; label: string; before: string; after: string }> {
  const labels: Record<keyof PriceParts, string> = {
    base: '基本料金', coupon: '割引', option: 'オプション料金', noc: 'NOC料金', cdw: 'CDW料金',
    other: 'その他（深夜早朝等）', sum: '合計',
  };
  return (Object.keys(labels) as Array<keyof PriceParts>)
    .filter((k) => k === 'sum' ? cur.sum !== next.sum : cur[k] !== next[k])
    .map((k) => ({ key: k, label: labels[k], before: yen(cur[k]), after: yen(next[k]) }));
}
